import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cccdAdmin, type PodiumResult } from '@/lib/api/cccdAdminClient';
import { inputCls } from '@/components/admin/FormComponents';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import Alert from '@/components/ui/alert/Alert';
import { toastError } from '@/lib/toast';
import { LoadState } from "@/components/ui/LoadState"

const POSITION_LABEL: Record<1 | 2 | 3, string> = { 1: '1° lugar', 2: '2° lugar', 3: '3° lugar' };

export function PodiumDiscountPicker({ teamId, onApplied }: { teamId: string; onApplied: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<PodiumResult | null>(null);

  // Una petición por pausa al escribir, no una por tecla.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['unused-podium-results', debouncedSearch],
    queryFn: () => cccdAdmin.getResults({ onlyUnused: true, search: debouncedSearch || undefined }),
    enabled: expanded,
  });
  // Nombre del equipo que recibe el descuento, para nombrarlo en la confirmación.
  const { data: receivingTeam } = useQuery({
    queryKey: ['cccd-team-name', teamId],
    queryFn: () => cccdAdmin.getTeam(teamId),
    enabled: expanded,
    staleTime: 60_000,
  });
  const results = data?.results ?? [];

  // Pesimista a propósito, no pendiente: escribe discountType/discountPercent
  // directamente en el pago del equipo — mutación financiera, excluida del
  // patrón optimistic UI (ver openspec/changes/gestion-optimistic-ui/design.md).
  const applyMutation = useMutation({
    mutationFn: (resultId: string) => cccdAdmin.applyPodiumDiscount(teamId, resultId),
    onSuccess: () => {
      setPending(null);
      setError('');
      qc.invalidateQueries({ queryKey: ['unused-podium-results'] });
      onApplied();
    },
    onError: (err: any) => {
      setPending(null);
      const message = err?.response?.data?.error ?? 'No se pudo aplicar el descuento del podio.';
      setError(message);
      toastError(message);
    },
  });

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="min-h-11 text-sm text-brand-500 hover:underline"
      >
        Aplicar descuento desde un podio…
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Podios sin usar</h4>
        <button type="button" onClick={() => setExpanded(false)} className="min-h-11 px-2 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400">Cerrar</button>
      </div>
      <input
        aria-label="Buscar podios sin usar"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por equipo, capitán, institución o categoría…"
        className={inputCls}
      />
      {error && <Alert variant="error" title="No se pudo aplicar el descuento" message={error} />}
      {isLoading ? (
        <LoadState />
      ) : results.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No hay podios sin usar que coincidan con la búsqueda.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {results.map((r: PodiumResult) => (
            <li key={r.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {r.team.name} — {POSITION_LABEL[r.position]} ({r.categorySlug})
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {r.event ? r.event.name : `${r.regionalEvent?.name} (${r.regionalEvent?.region})`}{r.team.captain ? ` · ${r.team.captain.name}` : ''}
                  {r.team.institution ? ` · ${r.team.institution.name}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPending(r)}
                disabled={applyMutation.isPending}
                className="min-h-11 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                Aplicar {r.discountPercent}%
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={pending !== null}
        danger={false}
        title={`¿Aplicar descuento de ${pending?.discountPercent ?? ''}%?`}
        message={`Se aplicará el descuento del podio de "${pending?.team.name ?? ''}" (${pending ? POSITION_LABEL[pending.position] : ''}, ${pending?.categorySlug ?? ''}) al pago de "${receivingTeam?.name ?? 'este equipo'}". Un descuento aplicado no se puede deshacer desde aquí.`}
        confirmLabel={`Aplicar ${pending?.discountPercent ?? ''}%`}
        confirmingLabel="Aplicando..."
        confirming={applyMutation.isPending}
        onConfirm={() => pending && applyMutation.mutate(pending.id)}
        onCancel={() => !applyMutation.isPending && setPending(null)}
      />
    </div>
  );
}
